/**
 * Shared Web Vitals sender for every PWA (#4022).
 *
 * WHY SHARED. Four clients need identical performance reporting. Four copies of
 * a fire-and-forget callable invocation is four places to drift, and the version
 * in `client/src/index.tsx` had already grown the defect below.
 *
 * WHAT CHANGED FROM THE ORIGINAL. The main client sent metrics with a bare
 * `.catch(() => {})` — the same swallow this project already removed from
 * `usageTracking.trackEvent`. A failing `logPerformanceMetric` was
 * indistinguishable from "the page was never slow", which is the worst possible
 * failure mode for the pipeline you consult to find out whether the app is slow.
 * That matters concretely here: `logPerformanceMetric` is one of the functions
 * #3934 found being OOM-killed by the 512MiB default, so it HAS been failing.
 *
 * Delivery stays fire-and-forget — a page must never block or break because
 * telemetry is down — but the drop is now COUNTED via the same
 * `deliveryFailures` counters the usage pipeline uses, so "delivered" and
 * "silently dropped" are distinguishable.
 *
 * SPECIFIER SAFETY. This module imports ONLY `firebase/functions`, which resolves
 * in all four clients (`firebase` is in every client's dedupe list). It imports
 * NO app-specific module: the earlier `../../config/firebase` import is what broke
 * the time-client build, because a relative path ignores the `@shared` alias and
 * always resolved to the MAIN app's config. It deliberately does NOT import
 * `web-vitals` either — the dynamic
 * import lives in `reportWebVitals.ts` and each client declares the dependency
 * itself. Shared code that introduces a bare specifier its consumers lack
 * externalises and white-screens a lazy chunk while the build stays green (the
 * 2026-07-30 `piexifjs` blank-FSR outage on technician.onpointgen.com).
 */
import { httpsCallable } from '../firebase/authReadyCallable';
import type { Functions } from 'firebase/functions';
import { recordAttempt, recordFailure } from './deliveryFailures';
import { isTelemetryDeliveryEnabled, sendWhenAuthed } from './authGate';

/** The shape `reportWebVitals` hands us — structurally the web-vitals `Metric`. */
export interface WebVitalMetric {
  name: string;
  value: number;
  rating?: string;
  delta?: number;
  navigationType?: string;
}

/**
 * Build a Web Vitals sender bound to ONE app's Firebase instance.
 *
 * 🔴 WHY THE INSTANCE IS PASSED IN RATHER THAN IMPORTED. The first version of
 * this module imported `functions` from `../../config/firebase`. Relative imports
 * do NOT go through the `@shared` alias, so that resolved to the MAIN app's
 * config for every consumer — the technician, time and sales apps would each have
 * reported their performance through client's Firebase instance rather than their
 * own. It also dragged `client/src/config/firebase.ts` into three other apps'
 * TypeScript programs, which broke the time-client build on main (13 of its 15
 * errors came from that one file, none of them from telemetry code).
 *
 * Shared code must not reach for app-specific singletons. The app owns its
 * config; this module owns the sending policy.
 *
 * Production only — dev noise would swamp the Performance tab and cost callable
 * invocations for numbers nobody reads. No sampling: volume is low enough to
 * capture every metric, which is what makes the tab trustworthy.
 */
export function createWebVitalSender(
  functions: Functions,
): (metric: WebVitalMetric) => void {
  return function sendWebVital(metric: WebVitalMetric): void {
    // Read through the helper, never inline: see isTelemetryDeliveryEnabled.
    if (!isTelemetryDeliveryEnabled()) return;

    // Snapshotted NOW, before the gate: `reportWebVitals` is wired at MODULE
    // LOAD and LCP/FCP/TTFB all fire during startup, so these events routinely
    // wait for auth to resolve. The route and timestamp must be the ones that
    // were true when the metric was measured, not when it was released.
    const payload = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      navigationType: metric.navigationType,
      route: window.location.pathname,
      timestamp: Date.now(),
    };

    // `logPerformanceMetric` is a withOrgAuth callable. Sending at module load
    // meant a guaranteed 401 on every page load — noise in the error channel,
    // and zero metrics from the startup window that Web Vitals exists to
    // measure. The gate buffers until Firebase auth resolves, then sends; if it
    // resolves with no user the event is dropped and COUNTED, never silently.
    sendWhenAuthed(() => {
      const logPerformanceMetric = httpsCallable(functions, 'logPerformanceMetric');
      recordAttempt();
      logPerformanceMetric(payload).catch(recordFailure);
    });
    // Still fire-and-forget; `recordFailure` never throws, so the non-blocking
    // contract is unchanged. What changed is that the drop is now countable.
  };
}
